/**
 * Services — data layer. Verbatim port of the canonical data-services.jsx.
 *
 * Three entities, one per Services tab:
 *   Service  — a reusable, bookable offering (events / appointments / queues)
 *   Category — an ordered grouping of services (reorder on its tab)
 *   Package  — a bundle of categories AND/OR individual services
 *
 * Admin-authored. Persistence: REQ-004 (CLAUDE-CODE-REQUESTS.md) is live —
 * this store hydrates from and persists through the real `/v1/admin/catalog/*`
 * client (briefings/services.md). An in-memory seed is kept only as a
 * same-session fallback for previews where the client isn't wired yet.
 * Every entity is Published / Draft — no approval gate.
 *
 * Ordering: `state.services` array order is canonical; within a category we
 * filter preserving that order.
 */
import { useEffect, useReducer } from "react";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, isSignedIn, onTenantChange, onTenantSwitching } from "../../shell/access";
import { t as i18nT } from "../../lib/i18n";
import {
  listServices, createStudioService, updateStudioService, deleteStudioService, duplicateStudioService, moveStudioService,
  listServiceCategories, createServiceCategory, updateServiceCategory, deleteServiceCategory, duplicateServiceCategory, reorderServiceCategories,
  listStudioPackages, createStudioPackage, updateStudioPackage, deleteStudioPackage, duplicateStudioPackage, reorderStudioPackages,
  type StudioServicesRequestOptions,
} from "@v0y4ge/api-client";

// baseUrl-carrying options required by the services list + package choke-points
// (packet #3 RC1). MSW intercepts by path in design-time, so a placeholder base
// is fine here; the real base is wired on deploy.
const REQUEST_OPTS: StudioServicesRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
};

export type SvcChannelId = "events" | "appointments" | "queues";
export interface SvcChannelDef { id: SvcChannelId; label: string; labelKey: string; icon: string; hue: string; }
export const SVC_CHANNELS: SvcChannelDef[] = [
  { id: "events",       labelKey: "services.data.events", get label() { return i18nT("services.data.events"); },       icon: "calendar", hue: "#5142C7" },
  { id: "appointments", labelKey: "services.data.appointments", get label() { return i18nT("services.data.appointments"); }, icon: "clock",    hue: "#1F8A5B" },
  { id: "queues",       labelKey: "services.data.queues", get label() { return i18nT("services.data.queues"); },       icon: "list",     hue: "#C77F14" },
];
export const SVC_CHANNEL = (id: string): SvcChannelDef => SVC_CHANNELS.find((c) => c.id === id) || SVC_CHANNELS[0];

export const SVC_MODES: Record<string, { label: string; labelKey: string; tone: string }> = {
  "in-person": { labelKey: "services.data.inPerson", get label() { return i18nT("services.data.inPerson"); }, tone: "neutral" },
  "virtual":   { labelKey: "services.data.virtual", get label() { return i18nT("services.data.virtual"); },   tone: "info" },
  "hybrid":    { labelKey: "services.data.hybrid", get label() { return i18nT("services.data.hybrid"); },    tone: "primary" },
};

/* Delivery methods — a service may support several; each carries its own
 * settings (duration, capacity, schedule, pricing, questions, comms, …). */
export interface SvcMethodDef { id: string; label: string; labelKey: string; sub: string; icon: string; hue: string; }
export const SVC_METHODS: SvcMethodDef[] = [
  { id: "in-person",         labelKey: "services.data.inPerson", get label() { return i18nT("services.data.inPerson"); },         sub: "Store / branch",   icon: "pin",    hue: "#5142C7" },
  { id: "online",            labelKey: "services.data.onlineMeeting", get label() { return i18nT("services.data.onlineMeeting"); },    sub: "Video",            icon: "video",  hue: "#1F8A5B" },
  { id: "live-chat",         labelKey: "services.data.liveTextChat", get label() { return i18nT("services.data.liveTextChat"); },    sub: "Real-time chat",   icon: "msg",    hue: "#0E7490" },
  { id: "live-screen",       labelKey: "services.data.liveScreenShare", get label() { return i18nT("services.data.liveScreenShare"); }, sub: "Co-browse / demo", icon: "layers", hue: "#8955F2" },
  { id: "phone",             labelKey: "services.data.phone", get label() { return i18nT("services.data.phone"); },             sub: "Phone call",       icon: "phone",  hue: "#2D60EB" },
  { id: "email",             labelKey: "services.data.email", get label() { return i18nT("services.data.email"); },             sub: "Asynchronous",     icon: "mail",   hue: "#B5791F" },
  { id: "customer-location", labelKey: "services.data.customerLocation", get label() { return i18nT("services.data.customerLocation"); }, sub: "Home / business",  icon: "loc",    hue: "#C13584" },
];
export const SVC_METHOD = (id: string): SvcMethodDef => SVC_METHODS.find((m) => m.id === id) || SVC_METHODS[0];
export const PAYMENT_TIMINGS = [{ id: "booking", labelKey: "services.data.atBooking", get label() { return i18nT("services.data.atBooking"); } }, { id: "visit", labelKey: "services.data.atTheVisit", get label() { return i18nT("services.data.atTheVisit"); } }, { id: "none", labelKey: "services.data.noPayment", get label() { return i18nT("services.data.noPayment"); } }];
export const SVC_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const SVC_AUDIENCES = [{ id: "everyone", labelKey: "services.data.everyone", get label() { return i18nT("services.data.everyone"); } }, { id: "members", labelKey: "services.data.membersOnly", get label() { return i18nT("services.data.membersOnly"); } }, { id: "vip", labelKey: "services.data.vipOnly", get label() { return i18nT("services.data.vipOnly"); } }, { id: "new", labelKey: "services.data.newCustomers", get label() { return i18nT("services.data.newCustomers"); } }];

export const _svcSlug = (s?: string) => (s || "service").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "service";

export type SvcMethodConfig = Record<string, any>;
export type Service = Record<string, any>;
export type SvcCategory = { id: string; name: string; description: string; hue: string; status: string; updated: string };
export type SvcPackageItem = { type: "category" | "service"; id: string };
export type SvcPackage = { id: string; name: string; description: string; status: string; hue: string; items: SvcPackageItem[]; updated: string };

/* Per-method config factory. Defaults a sensible weekly availability. */
export function svcDefaultMethodConfig(methodId: string, base?: { duration?: number; capacity?: number }): SvcMethodConfig {
  const wk: Record<string, { start: string; end: string }[]> = {};
  SVC_WEEKDAYS.forEach((d) => (wk[d] = (d === "Sat" || d === "Sun") ? [] : [{ start: "09:00", end: "17:00" }]));
  return {
    duration: (base && base.duration) || 30, pre: 0, post: 0, capacity: (base && base.capacity) || 1,
    pricing: { price: 0, deposit: 0, tax: 0, cancelFee: 0, noShowFee: 0, paymentTiming: "booking" },
    schedule: wk,
    questions: [],        // [{ kind:'group'|'question', id }]
    promos: [],           // [discountId]
    comms: { mode: "flow", flowId: "", custom: [] },
    // method-specific
    room: "", locations: [],                                  // in-person
    provider: "zoom", autoLink: true, joinInstructions: "",   // online
    whoCalls: "staff",                                        // phone
    sla: "1 business day", deliverable: "",                   // email
    driveTime: 30, travelRadius: 15, travelFee: 0, captureAddress: true, // customer-location
  };
}

/* Derive the enabled delivery methods from a legacy seed's `mode`. */
export function svcMethodsFromMode(mode?: string): string[] {
  if (mode === "virtual") return ["online"];
  if (mode === "hybrid") return ["in-person", "online"];
  return ["in-person"];
}

/* Normalize any service record to the full editable shape (fills defaults so
 * seeds open cleanly in the editor). Non-destructive. */
export function ensureServiceShape(s: Service): Service {
  const methods: string[] = (s.methods && s.methods.length) ? s.methods : svcMethodsFromMode(s.mode);
  const methodConfig: Record<string, SvcMethodConfig> = { ...(s.methodConfig || {}) };
  methods.forEach((m) => { methodConfig[m] = { ...svcDefaultMethodConfig(m, s), ...(methodConfig[m] || {}) }; });
  return {
    channels: ["appointments"], staff: 1, bookings: 0,
    ...s,
    methods,
    methodConfig,
    visibility: s.visibility || "public",
    confirmation: s.confirmation || "auto",            // 'auto' | 'review'
    landing: s.landing || { slug: _svcSlug(s.name), hero: { headline: s.name || "", blurb: "", image: "" } },
    staffRoles: s.staffRoles || [],
    staffGroups: s.staffGroups || [],
    staffing: s.staffing || [],           // [{ groupId, count }] — staff needed (optional)
    resourcing: s.resourcing || [],       // [{ groupId, count }] — resources needed (optional)
    resources: s.resources || [],
    concurrency: s.concurrency || 1,
    locScope: s.locScope || [],
    audience: s.audience || "everyone",
    addons: s.addons || [],
    prereqs: s.prereqs || [],
    tags: s.tags || [],
    exceptions: s.exceptions || [],       // [{ kind, label, from, to }]
    feedback: !!s.feedback,
    /* requiresPerson (kit delta 2026-09-11, JAI-1292).
       TRUE means availability comes from the assigned team's working hours.
       FALSE means hours attached to the service itself — and such a service is
       NOT offered on the public booking page.

       Defaulted here rather than left undefined, for two reasons. The backend
       defaults an omitted value to true on CREATE only, so an existing service
       read back without the field would flip meaning if we treated absent as
       false. And the toggle needs a definite value to render; `checked={undefined}`
       is an uncontrolled input. Non-appointment channels have no person to
       require, so they are false. */
    requiresPerson: typeof s.requiresPerson === "boolean"
      ? s.requiresPerson
      : (s.channels || ["appointments"]).includes("appointments"),
  };
}

/* Recompute the summary fields the list views read, from the rich shape. */
export function svcSyncSummary(s: Service): Service {
  const methods: string[] = s.methods || [];
  const remote = methods.every((m) => m === "online" || m === "phone" || m === "email" || m === "live-chat" || m === "live-screen");
  const physical = methods.every((m) => m === "in-person" || m === "customer-location");
  s.mode = methods.length === 0 ? (s.mode || "in-person") : (remote ? "virtual" : physical ? "in-person" : "hybrid");
  const first = methods[0] && s.methodConfig && s.methodConfig[methods[0]];
  if (first) { s.duration = first.duration; s.capacity = first.capacity; }
  return s;
}

let _svcSeq = 300;
const _svcId = (p: string) => `${p}_${(++_svcSeq).toString(36)}${Date.now().toString(36).slice(-3)}`;




/* ── Reactive store ─────────────────────────────────────────────────────── */
/**
 * 2026-07-03 (Claude Code push-back, §2): REQ-004 is live — full CRUD +
 * moveService/reorderCategory over `/v1/admin/catalog/*` (briefings/services.md).
 * This store now hydrates from + persists through that client instead of
 * localStorage. The in-memory seed below is kept ONLY as a same-session
 * fallback for when the client operations aren't present (this authoring
 * preview's fixture stub doesn't implement them yet) — it is never written
 * back to localStorage, and a successful hydrate() fully replaces it.
 */
interface SvcState { services: Service[]; categories: SvcCategory[]; packages: SvcPackage[]; }
let state: SvcState = {
  services:   [],
  categories: [],
  packages:   [],
};
const subs = new Set<() => void>();
const emit = () => { subs.forEach((fn) => fn()); };

function catFromDto(c: Record<string, any>): SvcCategory {
  return { id: c.id ?? c.categoryId, name: c.name ?? "", description: c.description ?? "", hue: c.hue ?? "#5142C7", status: c.status ?? "published", updated: c.updated ?? c.updatedAt ?? "" };
}
function pkgFromDto(p: Record<string, any>): SvcPackage {
  return { id: p.id ?? p.packageId, name: p.name ?? "", description: p.description ?? "", status: p.status ?? "published", hue: p.hue ?? "#5142C7", items: p.items ?? [], updated: p.updated ?? p.updatedAt ?? "" };
}

let _hydrated = false;
async function hydrate() {
  // Boot hygiene (2026-07-19 packet §2): this module is imported by screens.tsx
  // the instant the bundle loads — never fetch before a session exists. A
  // signed-out skip does NOT burn the flag; subscribe() re-invokes post-sign-in.
  if (_hydrated || !isSignedIn()) return;
  _hydrated = true;
  try {
    const [sr, cr, pr] = await Promise.all([
      typeof listServices === "function" ? listServices({}, REQUEST_OPTS) : Promise.resolve(null),
      typeof listServiceCategories === "function" ? listServiceCategories(REQUEST_OPTS) : Promise.resolve(null),
      typeof listStudioPackages === "function" ? listStudioPackages({}, REQUEST_OPTS) : Promise.resolve(null),
    ]);
    // Only overwrite each entity when the server actually returned rows —
    // an empty envelope (e.g. this preview harness's fixture stub) must not
    // stomp the in-memory seed, same guard as cal-categories.ts/integration-configs.ts.
    const srArr = sr ? (((sr as any).services ?? (sr as any).items ?? []) as Service[]) : [];
    const crArr = cr ? (((cr as any).categories ?? (cr as any).items ?? []) as any[]) : [];
    const prArr = pr ? (((pr as any).packages ?? (pr as any).items ?? []) as any[]) : [];
    const services = srArr.length ? srArr : state.services;
    const categories = crArr.length ? crArr.map(catFromDto) : state.categories;
    const packages = prArr.length ? prArr.map(pkgFromDto) : state.packages;
    if (srArr.length || crArr.length || prArr.length) {
      state = { services, categories, packages };
      emit();
    }
  } catch (e) { console.warn("Services hydrate failed, keeping in-memory fallback:", e); }
}
hydrate(); // no-op until signed in (§2)
// Tenant-switch handling (docket 2026-07-27, item 1): this store had NONE —
// tenant A's services/categories/packages stayed resident under tenant B's
// chrome after a switch. Purge immediately (`_hydrated = true` BLOCKS any
// incidental hydrate() call during the pending window, mirroring the guard
// hydrate() already checks); reload only once the new tenant is confirmed.
onTenantSwitching(() => { state = { services: [], categories: [], packages: [] }; _hydrated = true; emit(); });
// Capability gate (docket 2026-07-31): this re-hydrate is EAGER — it runs for
// every signed-in persona because screens.tsx imports this module, so an Event
// Manager was issuing listServices + listServiceCategories + listStudioPackages
// (3 of the 11 prefetch 403s) without ever opening a catalog screen. The flag
// still resets unconditionally so a later demand-driven subscribe() — the path
// the Services screens actually use, itself route-guarded on services.read —
// refetches normally for whoever does have the capability.
onTenantChange(() => { _hydrated = false; if (can("services.read")) hydrate(); });

export const ServicesStore = {
  subscribe(fn: () => void) { hydrate(); subs.add(fn); return () => { subs.delete(fn); }; },

  /* ── services ── */
  services: () => state.services.slice(),
  servicesIn: (catId: string | null) => state.services.filter((s) => s.category === catId),
  getService: (id: string) => state.services.find((s) => s.id === id) || null,
  async upsertService(s: Service) {
    // UIR-12 (2026-07-24): awaits the persist op BEFORE committing local state —
    // no-fake-interactivity mandate. Failure is logged AND rethrown so awaiting
    // callers get a real error state; callers that don't await keep the same
    // console signal as before (just via an unhandled-rejection warning instead
    // of a caught .catch()) — no behavior change for those uncited call sites.
    const rec = svcSyncSummary({ ...s });
    const isNew = !state.services.some((x) => x.id === rec.id);
    const i = state.services.findIndex((x) => x.id === rec.id);
    try {
      if (isNew && typeof createStudioService === "function") await createStudioService(rec as any, REQUEST_OPTS);
      else if (!isNew && typeof updateStudioService === "function") await updateStudioService(rec.id, rec as any, REQUEST_OPTS);
    } catch (e) {
      console.warn(isNew ? "createStudioService failed:" : "updateStudioService failed:", e);
      throw e;
    }
    logUpsert("service", isNew ? null : (state.services[i] as unknown as Record<string, unknown>), rec as unknown as Record<string, unknown>, { name: (rec as { name?: string }).name });
    if (i >= 0) state.services[i] = { ...rec, updated: "Just now" };
    else state.services = [{ ...rec, updated: "Just now" }, ...state.services];
    emit();
    return rec;
  },
  async duplicateService(id: string) {
    const src = state.services.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("svc"), name: src.name + " (copy)", status: "draft", bookings: 0, updated: "Just now" };
    if (typeof duplicateStudioService === "function") {
      try { await duplicateStudioService(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateStudioService failed:", e); throw e; }
    }
    const i = state.services.findIndex((x) => x.id === id);
    state.services.splice(i + 1, 0, copy); emit();
    return copy;
  },
  setServiceStatus: async (id: string, status: string) => {
    // Addendum item 16: await-before-commit (UIR-12 pattern at every mutation).
    if (typeof updateStudioService === "function") await updateStudioService(id, { status } as any, REQUEST_OPTS);
    const s = state.services.find((x) => x.id === id); if (s) { s.status = status; s.updated = "Just now"; emit(); }
  },
  removeService: async (id: string) => {
    if (typeof deleteStudioService === "function") await deleteStudioService(id, REQUEST_OPTS);
    const prev = state.services.find((x) => x.id === id);
    if (prev) logDelete("service", prev as unknown as Record<string, unknown>, { name: (prev as { name?: string }).name });
    state.services = state.services.filter((x) => x.id !== id); emit();
  },
  newService: (): Service => ensureServiceShape({ id: _svcId("svc"), name: "", category: "", channels: ["appointments"], methods: ["in-person"], mode: "in-person", duration: 30, capacity: 1, staff: 1, bookings: 0, status: "draft", updated: "Just now" }),
  countInCategory: (catId: string) => state.services.filter((s) => s.category === catId).length,
  /* Move a service to a category, inserting before `beforeId` (or at end). */
  moveService: async (serviceId: string, toCategoryId: string, beforeId: string | null = null) => {
    const arr = state.services;
    const from = arr.findIndex((s) => s.id === serviceId); if (from < 0) return;
    // Addendum item 16: persist first; reorder locally only on ack.
    if (typeof moveStudioService === "function") await moveStudioService({ serviceId, toCategoryId, beforeServiceId: beforeId }, REQUEST_OPTS);
    const [svc] = arr.splice(from, 1);
    svc.category = toCategoryId; svc.updated = "Just now";
    let insertAt: number;
    if (beforeId) { insertAt = arr.findIndex((s) => s.id === beforeId); if (insertAt < 0) insertAt = arr.length; }
    else { let last = -1; arr.forEach((s, i) => { if (s.category === toCategoryId) last = i; }); insertAt = last + 1; }
    arr.splice(insertAt, 0, svc); emit();
  },

  /* ── categories ── */
  categories: () => state.categories.slice(),
  getCategory: (id: string) => state.categories.find((c) => c.id === id) || null,
  async upsertCategory(c: SvcCategory) {
    const isNew = !state.categories.some((x) => x.id === c.id);
    // Addendum item 16: await-before-commit.
    if (isNew && typeof createServiceCategory === "function") await createServiceCategory(c as any, REQUEST_OPTS);
    else if (!isNew && typeof updateServiceCategory === "function") await updateServiceCategory(c.id, c as any, REQUEST_OPTS);
    const i = state.categories.findIndex((x) => x.id === c.id);
    logUpsert("serviceCategory", isNew ? null : (state.categories[i] as unknown as Record<string, unknown>), c as unknown as Record<string, unknown>, { name: c.name });
    if (i >= 0) state.categories[i] = { ...c, updated: "Just now" };
    else state.categories = [...state.categories, { ...c, updated: "Just now" }];
    emit();
    return c;
  },
  async duplicateCategory(id: string) {
    const src = state.categories.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("cat"), name: src.name + " (copy)", status: "draft", updated: "Just now" };
    if (typeof duplicateServiceCategory === "function") {
      try { await duplicateServiceCategory(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateServiceCategory failed:", e); throw e; }
    }
    state.categories = [...state.categories, copy]; emit();
    return copy;
  },
  setCategoryStatus: async (id: string, status: string) => {
    if (typeof updateServiceCategory === "function") await updateServiceCategory(id, { status } as any, REQUEST_OPTS);
    const c = state.categories.find((x) => x.id === id); if (c) { c.status = status; c.updated = "Just now"; emit(); }
  },
  removeCategory: async (id: string) => {
    if (typeof deleteServiceCategory === "function") await deleteServiceCategory(id, REQUEST_OPTS);
    state.categories = state.categories.filter((x) => x.id !== id);
    state.services.forEach((s) => { if (s.category === id) s.category = null; });
    emit();
  },
  newCategory: (): SvcCategory => ({ id: _svcId("cat"), name: "", description: "", hue: "#5142C7", status: "draft", updated: "Just now" }),
  reorderCategory(id: string, dir: "up" | "down") {
    const i = state.categories.findIndex((c) => c.id === id); if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1; if (j < 0 || j >= state.categories.length) return;
    const arr = state.categories; [arr[i], arr[j]] = [arr[j], arr[i]]; emit();
    if (typeof reorderServiceCategories === "function") reorderServiceCategories({ categoryIds: state.categories.map((c) => c.id) }, REQUEST_OPTS).catch((e: unknown) => console.warn("reorderServiceCategories failed:", e));
  },

  /* ── packages ── */
  packages: () => state.packages.slice(),
  getPackage: (id: string) => state.packages.find((p) => p.id === id) || null,
  async upsertPackage(p: SvcPackage) {
    const isNew = !state.packages.some((x) => x.id === p.id);
    const i = state.packages.findIndex((x) => x.id === p.id);
    const clean = { ...p, items: (p.items || []).map((i2) => ({ ...i2 })), updated: "Just now" };
    // Addendum item 16 (final residue): await-before-commit.
    if (isNew && typeof createStudioPackage === "function") await createStudioPackage(clean as any, REQUEST_OPTS);
    else if (!isNew && typeof updateStudioPackage === "function") await updateStudioPackage(clean.id, clean as any, REQUEST_OPTS);
    if (i >= 0) state.packages[i] = clean;
    else state.packages = [clean, ...state.packages];
    emit();
    return clean;
  },
  async duplicatePackage(id: string) {
    const src = state.packages.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("pkg"), name: src.name + " (copy)", status: "draft", updated: "Just now", items: src.items.map((i) => ({ ...i })) };
    if (typeof duplicateStudioPackage === "function") {
      try { await duplicateStudioPackage(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateStudioPackage failed:", e); throw e; }
    }
    state.packages = [copy, ...state.packages]; emit();
    return copy;
  },
  setPackageStatus: async (id: string, status: string) => {
    if (typeof updateStudioPackage === "function") await updateStudioPackage(id, { status } as any, REQUEST_OPTS);
    const p = state.packages.find((x) => x.id === id); if (p) { p.status = status; p.updated = "Just now"; emit(); }
  },
  removePackage: async (id: string) => {
    if (typeof deleteStudioPackage === "function") await deleteStudioPackage(id, REQUEST_OPTS);
    state.packages = state.packages.filter((x) => x.id !== id); emit();
  },
  newPackage: (): SvcPackage => ({ id: _svcId("pkg"), name: "", description: "", status: "draft", hue: "#5142C7", items: [], updated: "Just now" }),
  /* Count of distinct services a package resolves to. */
  packageServiceCount(pkg: SvcPackage) {
    const ids = new Set<string>();
    (pkg.items || []).forEach((it) => {
      if (it.type === "category") state.services.filter((s) => s.category === it.id).forEach((s) => ids.add(s.id));
      else ids.add(it.id);
    });
    return ids.size;
  },
  /* Reorder packages in the library list (docket 2026-07-28: reorderStudioPackages
   * was imported but never called). Mirrors reorderCategory's optimistic-then-persist shape. */
  reorderPackage(id: string, dir: "up" | "down") {
    const i = state.packages.findIndex((p) => p.id === id); if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1; if (j < 0 || j >= state.packages.length) return;
    const arr = state.packages; [arr[i], arr[j]] = [arr[j], arr[i]]; emit();
    if (typeof reorderStudioPackages === "function") reorderStudioPackages({ packageIds: state.packages.map((p) => p.id) }, REQUEST_OPTS).catch((e: unknown) => console.warn("reorderStudioPackages failed:", e));
  },
};

export function useServices() {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => ServicesStore.subscribe(force), []);
  return ServicesStore;
}

/* Resolvers used across the screens. */
export const svcCategoryName = (id?: string | null) => !id ? "None" : ((ServicesStore.getCategory(id) || ({} as SvcCategory)).name || "Uncategorised");
export const svcCategoryHue  = (id?: string | null) => (id && ServicesStore.getCategory(id) ? ServicesStore.getCategory(id)!.hue : "#94A3B8");
export const svcDurationLabel = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`;
